// Authentication types
export interface User {
  id: string;
  google_id?: string;
  email: string;
  name: string;
  avatar_url?: string;
  phone?: string;
  email_verified: boolean;
  subscription_tier: 'basic' | 'advanced';
  subscription_expires_at?: string;
  last_login_at?: string;
  created_at: string;
  updated_at: string;
  isGrowthAdmin?: boolean;
  isUnlistedAdmin?: boolean;
}

export interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
}

// API response types
export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: {
    message: string;
    code?: string;
    fields?: Record<string, string>;
  };
  pagination?: {
    total: number;
    limit: number;
    offset: number;
  };
  timestamp: string;
}

// Broker account types
export interface BrokerAccount {
  id: string;
  user_id: string;
  broker_name: string;
  nickname: string;
  sync_status: 'pending' | 'success' | 'error';
  last_sync_at?: string;
  error_message?: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

// Holdings types
export interface Holding {
  id: string;
  broker_account_id: number;
  symbol: string;
  exchange: string;
  quantity: number;
  average_price: number;
  current_price?: number;
  market_value?: number;
  pnl?: number;
  pnl_percent?: number;
  last_updated: string;
  isin?: string;
  company_name?: string;
  // Additional calculated fields
  purchase_value?: number;
  day_change?: number;
  day_change_percent?: number;
  broker_type?: string;
  broker_name?: string;
}

// Transaction types
export interface Transaction {
  id: string;
  account_id: string;
  symbol: string;
  transaction_type: 'buy' | 'sell';
  quantity: number;
  price: number;
  date: string;
  created_at: string;
}

// Alert types
export interface Alert {
  id: string;
  user_id: string;
  broker_account_id?: number;
  symbol: string;
  alert_type: 'price_above' | 'price_below' | 'volume_spike' | 'percentage_change';
  condition: string;
  target_value: number;
  current_value?: number;
  is_active: boolean;
  triggered_at?: string;
  message?: string;
  notification_methods: {
    email: boolean;
    push: boolean;
  };
  created_at: string;
  updated_at: string;
}

// Navigation types
export interface NavItem {
  label: string;
  href: string;
  icon?: React.ComponentType;
}

// PositionRule types
export interface PositionRule {
  id: string;
  user_id: string;
  symbol: string;
  exchange: string;
  rule_name: string;
  rule_scope: 'all_holdings' | 'specific_account';
  target_account_id?: number;

  // Rule configuration
  action_type: 'SELL' | 'ALERT';
  trigger_type: 'TRAILING_STOP_LOSS' | 'FIXED_STOP_LOSS';
  trigger_value: number;
  value_type?: 'PERCENT' | 'ABSOLUTE';
  // Sell order specifics (for SELL action type)
  sell_quantity_type?: 'FIXED_AMOUNT' | 'PERCENTAGE' | 'ALL_REMAINING';
  sell_quantity_value?: number;
  order_type?: 'MARKET' | 'LIMIT';
  limit_price?: number;

  // Rule status and tracking
  is_active: boolean;
  priority: number;
  created_at: string;
  updated_at: string;
  last_checked_at?: string;
  last_triggered_at?: string;
  execution_count: number;
  // Calculated price at which the rule will trigger (LTP adjusted for stop-loss)
  computed_trigger_price?: number;
}

// Account-level holding details for aggregated tables
export interface AccountHolding {
  id?: string; // Holding ID for editing (only for user-added holdings)
  broker_account_id: number;
  broker_name: string;
  broker_type: string;
  quantity: number;
  average_price: number;
  current_price?: number;
  market_value?: number;
  pnl?: number;
  pnl_percent?: number;
  purchase_value: number; // Always computed as quantity * average_price
  active_rules_count: number;
}

// Aggregated holding at symbol level (across accounts)
export interface AggregatedHolding {
  symbol: string;
  exchange: string;
  company_name?: string;
  total_quantity: number;
  weighted_avg_price: number;
  total_investment: number;
  total_current_value: number;
  total_pnl: number;
  total_pnl_percent: number;
  accounts: AccountHolding[];
  active_rules_count: number;
}

// Rule template used in templates modal
export interface RuleTemplate {
  id: string;
  name: string;
  description?: string;
  action_type: PositionRule['action_type'];
  trigger_type: PositionRule['trigger_type'];
  trigger_value: number;
  sell_quantity_type?: PositionRule['sell_quantity_type'];
  sell_quantity_value?: number;
  order_type?: PositionRule['order_type'];
}

// Execution log for rule executions
export interface RuleExecution {
  id: string;
  rule_id: string;
  user_id: string;
  symbol: string;
  exchange: string;
  rule_scope?: string;
  action_type: string;
  trigger_type: string;
  trigger_value: number;
  value_type?: string;
  execution_price: number;
  executed_at: string;
  status: string;
  message?: string;
  // Additional execution details
  execution_type?: 'SELL_ORDER' | 'ALERT';
  trigger_price?: number;
  account_id?: number;
  quantity_executed?: number;
  broker_order_id?: string;
  error_message?: string;
}

// Symbol Management Types
export interface SymbolSearchResult {
  symbol: string;
  company_name?: string;
  exchange: 'NSE' | 'BSE';
  isin?: string;
  market_cap_category?: string;
  relevance_score?: number;
}

export interface SymbolDetails {
  id: string;
  symbol: string;
  company_name?: string;
  isin?: string;
  exchange: 'NSE' | 'BSE';
  series?: string;
  market_cap_category?: string;
  is_active: boolean;
  last_updated: string;
  created_at: string;
}

export interface SymbolValidationResponse {
  valid: boolean;
  symbol?: string;
  company_name?: string;
  exchange?: 'NSE' | 'BSE';
  suggestions?: string[];
  error_message?: string;
}

export interface SymbolSyncResult {
  exchange: 'NSE' | 'BSE';
  status: 'pending' | 'running' | 'completed' | 'failed';
  symbols_count: number;
  error_message?: string;
  sync_duration_ms?: number;
  last_sync_at?: string;
}

// Unlisted Stocks
export const UnlistedStockStatus = {
  ACTIVE: 'ACTIVE',
  SOLD_OUT: 'SOLD_OUT',
  LISTED: 'LISTED',
  HIDDEN: 'HIDDEN'
} as const;

export type UnlistedStockStatus = typeof UnlistedStockStatus[keyof typeof UnlistedStockStatus];

export interface PricingSlab {
  min_qty: number;
  price: number;
}

export interface UnlistedStock {
  id: string;
  name: string;
  symbol: string;
  slug: string;
  sector: string;
  price: number;
  currency: string;
  change_percent: number;
  min_investment: number;
  description: string;
  status: UnlistedStockStatus;
  is_featured: boolean;
  image_url?: string;
  pricing_slabs?: PricingSlab[];
  created_at: string;
  updated_at: string;
}

// Murmurs types
export interface Murmur {
  id: string;
  symbol: string;
  price: number;
  timestamp: string;
  value_crores: number;
  volume?: number;
  minute_multiple?: number;
  projected_multiplier?: number;
  buy_qty?: number;
  sell_qty?: number;
  created_at: string;
}